"use client";

// Full-screen view for a centre gallery.
//
// Rendered through a portal onto <body> rather than in place. The gallery sits
// inside a rounded, overflow-hidden frame and inside the page's reveal
// animations, and a transformed ancestor makes position:fixed resolve against
// that ancestor instead of the viewport — the overlay would be clipped into
// the gallery's own box. A portal removes the question.
import { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { youtubeId } from "./MediaCarousel";

export function MediaLightbox({
  items,
  index,
  alt = "",
  onIndex,
  onClose,
}: {
  items: string[];
  index: number;
  alt?: string;
  onIndex: (i: number) => void;
  onClose: () => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  // Where focus was when this opened, so it can go back there. Without it a
  // keyboard reader lands at the top of the document on close, having lost
  // the gallery they were looking at.
  const returnTo = useRef<Element | null>(null);

  const go = useCallback(
    (i: number) => onIndex((i + items.length) % items.length),
    [items.length, onIndex],
  );

  useEffect(() => {
    returnTo.current = document.activeElement;
    closeRef.current?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") go(index + 1);
      else if (e.key === "ArrowLeft") go(index - 1);
    };
    document.addEventListener("keydown", onKey);

    // Hold the page still underneath. Replacing the scrollbar's width with
    // padding stops the layout jumping sideways as it disappears.
    const { overflow, paddingRight } = document.body.style;
    const gap = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    if (gap > 0) document.body.style.paddingRight = `${gap}px`;

    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
      document.body.style.paddingRight = paddingRight;
      (returnTo.current as HTMLElement | null)?.focus?.();
    };
  }, [index, go, onClose]);

  const src = items[index];
  if (!src) return null;
  const video = youtubeId(src);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Gallery"
      // The backdrop closes. The check is on the target rather than a
      // stopPropagation inside, so a click that STARTS on the picture and ends
      // on the backdrop — a drag, a slipped mouse — does not count as a click
      // outside and dismiss the thing being looked at.
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4 md:p-10"
    >
      <button
        ref={closeRef}
        type="button"
        onClick={onClose}
        aria-label="Close gallery"
        className="absolute right-4 top-4 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white outline-hidden transition-colors hover:bg-white/20 focus-visible:ring-2 focus-visible:ring-white md:right-6 md:top-6"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden>
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      </button>

      {/* The media itself. pointer-events-none on the <img> would stop the
          backdrop test seeing it, so instead it simply is not the backdrop. */}
      {video ? (
        <iframe
          src={`https://www.youtube-nocookie.com/embed/${video}?autoplay=1`}
          title="Video"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          className="aspect-video w-full max-w-5xl rounded-lg"
        />
      ) : (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={src}
          alt={alt}
          className="max-h-full max-w-full rounded-lg object-contain"
        />
      )}

      {items.length > 1 && (
        <>
          <button
            type="button"
            onClick={() => go(index - 1)}
            aria-label="Previous"
            className="absolute left-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white outline-hidden transition-colors hover:bg-white/20 focus-visible:ring-2 focus-visible:ring-white md:left-6"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M15 18l-6-6 6-6" /></svg>
          </button>
          <button
            type="button"
            onClick={() => go(index + 1)}
            aria-label="Next"
            className="absolute right-3 top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white outline-hidden transition-colors hover:bg-white/20 focus-visible:ring-2 focus-visible:ring-white md:right-6"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M9 6l6 6-6 6" /></svg>
          </button>
          <p className="mono-meta absolute bottom-5 left-1/2 -translate-x-1/2 text-white/70">
            {index + 1} / {items.length}
          </p>
        </>
      )}
    </div>,
    document.body,
  );
}
